import * as React from 'react';
import { Button} from 'reactstrap';
import { IDataChartOptions } from '../../components/ui/shared/Charts/interfaces';
import { DataSource } from '../../components/ui/shared/DataSource';
import { HttpRequest } from '../../util/http';
import { userData } from '../../util/userData';
import { IUserSettingsData } from './interfaces';
import Spinner from 'reactstrap/lib/Spinner';
import Alert from 'reactstrap/lib/Alert';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { GridContainer } from 'components/ui/Grid';
import './index.css'
import Input from 'reactstrap/lib/Input';

interface IUserSettingsReportProps {
  objectKey: string
  container: string
  project: string
}

interface IUserSettingsReportState {
  UserSettingsDataSource?: DataSource<IUserSettingsData, IDataChartOptions>
  canExport: boolean;
  exportLoaded: boolean;
  callingTheApi: boolean;
  apiCallSuccess: boolean;
  gettingDataError: Error | null;
  userRecords: IUserSettingsData[];
  searchText: string;
}

export class UserSettingsReportContainer extends React.Component<IUserSettingsReportProps, IUserSettingsReportState> {
  state: IUserSettingsReportState = {
    UserSettingsDataSource: null,
    canExport: false,
    exportLoaded: true,
    callingTheApi: false,
    apiCallSuccess: true,
    gettingDataError: null,
    userRecords: [],
    searchText: ''
  }

  private gridApi: any;
  private gridColumnApi: any;

  componentWillMount() {
    const identity = userData.getUserDataFromSession('token')

    this.loadReport(identity.session, identity.userId, identity.version)
    this.loadExportAccess()
  }

  loadExportAccess() {
    HttpRequest.fetch<{ canExport: boolean }>('/scripts/coaching_forms/get_export_permission').then((resp) => this.setState({canExport: resp.canExport }))
  }

  loadReport(sessionKey, userId, version) {
    const UserSettingsDataSource = new DataSource<IUserSettingsData, IDataChartOptions>('/scripts/users/user_summary_report', {
      method: 'POST',
      body: JSON.stringify({ objectKey: this.props.objectKey, version, sessionKey, user_id: userId, filters: [{ id: 'project', value: userData.getUserDataFromSession('current_project') }] }),
      recordsPath: 'data.data',
      visualizationPath: 'visualization',
    })

    this.setState({ UserSettingsDataSource }, () => this.syncUserSettings())
  }

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    this.setState({ searchText }, this.applySearchFilter);
  }

  applySearchFilter = () => {
    if (!this.gridApi) return;
    
    this.gridApi.setQuickFilter(this.state.searchText);
  }

  exportUserSummaryCSV = () => {
    if (this.gridApi) {
      const params = {
        fileName: `user_summary_settings_${new Date().toISOString().split('T')[0]}.csv`,
        suppressQuotes: true
      };
      
      this.gridApi.exportDataAsCsv(params);
    }
  }

  onGridReady = (params: any) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    
    if (params.api) {
      params.api.sizeColumnsToFit();
    }
  }

  onFirstDataRendered = (params: any) => {
    params.api.sizeColumnsToFit();
  }

  syncUserSettings() {
    this.setState({ callingTheApi: true });
    
    if (this.state.UserSettingsDataSource !== undefined) {
      this.state.UserSettingsDataSource.onSync((records: IUserSettingsData[], visualization: IDataChartOptions) => {
        this.setState({
          userRecords: records,
          callingTheApi: false,
          apiCallSuccess: true
        });
      });
      
      this.state.UserSettingsDataSource.sync();
    }
  }

  render() {
    const { callingTheApi, apiCallSuccess, gettingDataError, searchText } = this.state;
  
    const columnDefs: ColDef[] = [
      { 
        headerName: 'Username', 
        field: 'username',
        sortable: true,
        filter: true,
        cellRenderer: (params) => {
          const anchor = `user_manage?userId=${params.data.ID}`;
          return `<a href="${anchor}">${params.value}</a>`;
        }
      },
      { headerName: 'Email', field: 'Email', sortable: true, filter: true },
      { headerName: 'Dashboard Access', field: 'dashboardAccess', sortable: true, filter: true },
      { headerName: 'Individual Surveys Access', field: 'coachingAccess', sortable: true, filter: true },
      { headerName: 'Notification Access', field: 'notificationAccess', sortable: true, filter: true },
      { headerName: 'Configured Dashboards', field: 'dashboards', sortable: true, filter: true },
      { headerName: 'Will Receive Notifications', field: 'receiveNotifications', sortable: true, filter: true },
      { 
        headerName: 'User Admin', 
        field: 'isUserAdmin', 
        sortable: true, 
        filter: true 
      },
      { 
        headerName: 'MFA Status', 
        field: 'mfaStatus', 
        sortable: true, 
        filter: true 
      }
    ];

    const defaultColDef = {
      resizable: true,
      width: 150
    };

    return (
      <GridContainer>
        <div className="user-setttings-summary-title">
          <h3>User Settings Summary</h3>
          <Input 
            placeholder='Search...' 
            value={searchText}
            onChange={this.handleSearchChange}
            className='user-settings-summary-search'
          />
          {gettingDataError && 
            <Alert color="warning">
              There was an error: {gettingDataError.message}
            </Alert>
          }
          <Button 
            color="primary"
            onClick={this.exportUserSummaryCSV}
            disabled={callingTheApi || !this.state.UserSettingsDataSource}
          >
            Export User Settings Summary
          </Button>
        </div>
        
        {callingTheApi && (
          <div className="loading-bar-container">
            <div className="loading-bar"></div>
          </div>
        )}
        
        <div 
          className="ag-theme-balham default-grid-size"
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={this.state.userRecords}
            defaultColDef={defaultColDef}
            onGridReady={this.onGridReady}
            onFirstDataRendered={this.onFirstDataRendered}
          />
        </div>
      </GridContainer>
    )
  }
}
